<script lang="ts">
    import {onMount} from 'svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';
    import {Eye, EyeSlash} from 'phosphor-svelte';
    import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
    import ClientIntegrationSettings
        from '$lib/components/drawers/client/integrations/ClientIntegrationSettings.svelte';
    import FieldHint from '$lib/components/drawers/client/integrations/FieldHint.svelte';
    import SyncSettings from '$lib/components/drawers/client/integrations/SyncSettings.svelte';

    export let clientId: number;
  export let clientKey: string | undefined;
  export let organisationId: string | undefined;

  type TassIntegration = {
    aliiClientId: number;
    aliiClientKey: string;
    aliiEntityId: number;
    aliiEntityKey: string;
    tassCompanyCode: string;
    tassSchoolName: string;
    tassUrl: string;
    tassApplicationCodeVersion2: string;
    tassTokenKeyVersion2: string;
    tassApplicationCodeVersion3: string;
    tassTokenKeyVersion3: string;
    canSync: boolean;
    canInvoicePost: boolean;
    canExpenseClaimPost: boolean;
    syncTaxCodes: boolean;
    syncSuppliers: boolean;
    syncSupplierLineDefaults: boolean;
    syncGeneralLedgerCodes: boolean;
    syncGeneralLedgerCodeBusinessUnits: boolean;
    syncGeneralLedgerCodeUsers: boolean;
    syncBusinessUnits: boolean;
    syncBudgets: boolean;
    syncSpends: boolean;
    syncUsers: boolean;
    budgetNumber: number;
    generalLedgerCodeType: string | null;
    mergeEntityDepartment: string | null;
    supplierTeacherKiosk: boolean | null | undefined;
    useAliiInvoicePostingDate: boolean;
    attachAliiInvoiceToTass: boolean;
    ignoreDueDate: boolean;
    lastSyncRunUtc: string | null;
    lastSyncErrorUtc: string | null;
    lastDataSyncErrorUtc: string | null;
    lastDeleteAbortedUtc: string | null;
    createdAtUtc: string;
    hasEntityConfig: boolean;
  };

  type EntityRow = {
    id: number;
    key: string;
    name: string;
    code: string;
    integration: TassIntegration | null;
  };

  type CredForm = {
    tassUrl: string;
    tassCompanyCode: string;
    tassSchoolName: string;
    tassApplicationCodeVersion2: string;
    tassTokenKeyVersion2: string;
    tassApplicationCodeVersion3: string;
    tassTokenKeyVersion3: string;
    syncSettings: Record<string, boolean>;
    showAppCodeV2: boolean;
    showTokenKeyV2: boolean;
    showAppCodeV3: boolean;
    showTokenKeyV3: boolean;
  };

  type ConfigForm = {
    budgetNumber: number;
    generalLedgerCodeType: string;
    mergeEntityDepartment: string;
    supplierTeacherKiosk: boolean;
    useAliiInvoicePostingDate: boolean;
    attachAliiInvoiceToTass: boolean;
    ignoreDueDate: boolean;
  };

  type SyncSetting = {
    key: string;
    label: string;
  };

  let entities: EntityRow[] = [];
  let isLoading = false;
  let isRefreshing = false;
  let isForcingSync = false;
  let isSavingCred: Record<number, boolean> = {};
  let isSavingConfig: Record<number, boolean> = {};
  let expandedId: number | null = null;
  let credForms: Record<number, CredForm> = {};
  let configForms: Record<number, ConfigForm> = {};
  $: hasConfiguredEntities = entities.some((entity) => entity.integration);
  const customSyncSettings: SyncSetting[] = [
    { key: 'syncSupplierLineDefaults', label: 'Supplier Line Defaults' },
    { key: 'syncGeneralLedgerCodeBusinessUnits', label: 'GL Code Business Units' },
    { key: 'syncGeneralLedgerCodeUsers', label: 'GL Code Users' },
    { key: 'syncBusinessUnits', label: 'Business Units' },
    { key: 'syncUsers', label: 'Users' },
  ];

  onMount(load);

  async function load(preserveScroll = false, showLoading = true) {
    if (isLoading || isRefreshing) return;
    if (showLoading) {
      isLoading = true;
    } else {
      isRefreshing = true;
    }
    const scrollY = preserveScroll ? window.scrollY : 0;
    try {
      const res = await fetchWithAuth(`/api/tass-v2/entities/${clientId}`);
      if (res.ok) {
        entities = await res.json();
      } else showToast({ title: 'Error', message: 'Failed to load entities', type: 'error' });
    } catch {
      showToast({ title: 'Error', message: 'Failed to load entities', type: 'error' });
    } finally {
      isLoading = false;
      isRefreshing = false;
      if (preserveScroll) requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }));
    }
  }

  function openRow(entity: EntityRow) {
    if (expandedId === entity.id) {
      expandedId = null;
      return;
    }
    expandedId = entity.id;
    const i = entity.integration;
    credForms[entity.id] = {
      tassUrl: i?.tassUrl ?? '',
      tassCompanyCode: i?.tassCompanyCode ?? '',
      tassSchoolName: i?.tassSchoolName ?? '',
      tassApplicationCodeVersion2: i?.tassApplicationCodeVersion2 ?? '',
      tassTokenKeyVersion2: i?.tassTokenKeyVersion2 ?? '',
      tassApplicationCodeVersion3: i?.tassApplicationCodeVersion3 ?? '',
      tassTokenKeyVersion3: i?.tassTokenKeyVersion3 ?? '',
      syncSettings: {
        canSync: i?.canSync ?? true,
        canInvoicePost: i?.canInvoicePost ?? true,
        canExpenseClaimPost: i?.canExpenseClaimPost ?? true,
        syncTaxCodes: i?.syncTaxCodes ?? true,
        syncSuppliers: i?.syncSuppliers ?? true,
        syncSupplierLineDefaults: i?.syncSupplierLineDefaults ?? false,
        syncGeneralLedgerCodes: i?.syncGeneralLedgerCodes ?? true,
        syncGeneralLedgerCodeBusinessUnits: i?.syncGeneralLedgerCodeBusinessUnits ?? true,
        syncGeneralLedgerCodeUsers: i?.syncGeneralLedgerCodeUsers ?? true,
        syncBusinessUnits: i?.syncBusinessUnits ?? false,
        syncBudgets: i?.syncBudgets ?? true,
        syncSpends: i?.syncSpends ?? true,
        syncUsers: i?.syncUsers ?? true,
      },
      showAppCodeV2: false,
      showTokenKeyV2: false,
      showAppCodeV3: false,
      showTokenKeyV3: false,
    };
    configForms[entity.id] = {
      budgetNumber: i?.budgetNumber ?? 1,
      generalLedgerCodeType: i?.generalLedgerCodeType ?? '',
      mergeEntityDepartment: i?.mergeEntityDepartment ?? '',
      supplierTeacherKiosk: i?.supplierTeacherKiosk ?? false,
      useAliiInvoicePostingDate: i?.useAliiInvoicePostingDate ?? false,
      attachAliiInvoiceToTass: i?.attachAliiInvoiceToTass ?? true,
      ignoreDueDate: i?.ignoreDueDate ?? false,
    };
  }

  async function saveCred(entity: EntityRow, isAutoSave = false) {
    const f = credForms[entity.id];
    if (!f?.tassUrl || !f?.tassCompanyCode || !clientKey) return;
    isSavingCred = { ...isSavingCred, [entity.id]: true };
    try {
      const res = await fetchWithAuth('/api/tass-v2/configure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientKey,
          entityId: entity.id,
          entityKey: entity.key,
          tassUrl: f.tassUrl,
          tassCompanyCode: f.tassCompanyCode,
          tassSchoolName: f.tassSchoolName,
          tassApplicationCodeVersion2: f.tassApplicationCodeVersion2,
          tassTokenKeyVersion2: f.tassTokenKeyVersion2,
          tassApplicationCodeVersion3: f.tassApplicationCodeVersion3,
          tassTokenKeyVersion3: f.tassTokenKeyVersion3,
          canSync: f.syncSettings.canSync,
          canInvoicePost: f.syncSettings.canInvoicePost,
          canExpenseClaimPost: f.syncSettings.canExpenseClaimPost,
          syncTaxCodes: f.syncSettings.syncTaxCodes,
          syncSuppliers: f.syncSettings.syncSuppliers,
          syncSupplierLineDefaults: f.syncSettings.syncSupplierLineDefaults,
          syncGeneralLedgerCodes: f.syncSettings.syncGeneralLedgerCodes,
          syncGeneralLedgerCodeBusinessUnits: f.syncSettings.syncGeneralLedgerCodeBusinessUnits,
          syncGeneralLedgerCodeUsers: f.syncSettings.syncGeneralLedgerCodeUsers,
          syncBusinessUnits: f.syncSettings.syncBusinessUnits,
          syncBudgets: f.syncSettings.syncBudgets,
          syncSpends: f.syncSettings.syncSpends,
          syncUsers: f.syncSettings.syncUsers,
        }),
      });
      if (res.ok) {
        showToast({ title: 'Success', message: isAutoSave ? 'Tass sync settings saved' : 'Credentials saved', type: 'success' });
        await load(true, false);
      } else {
        showToast({ title: 'Error', message: isAutoSave ? 'Failed to save Tass sync settings' : 'Failed to save credentials', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: isAutoSave ? 'Failed to save Tass sync settings' : 'Failed to save credentials', type: 'error' });
    } finally {
      isSavingCred = { ...isSavingCred, [entity.id]: false };
    }
  }

  async function saveConfig(entity: EntityRow) {
    const f = configForms[entity.id];
    if (!f || !clientKey || !entity.integration) return;
    isSavingConfig = { ...isSavingConfig, [entity.id]: true };
    try {
      const res = await fetchWithAuth(`/api/tass-v2/config/${clientId}/${entity.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientKey,
          entityId: entity.id,
          entityKey: entity.key,
          budgetNumber: f.budgetNumber,
          generalLedgerCodeType: f.generalLedgerCodeType || null,
          mergeEntityDepartment: f.mergeEntityDepartment || null,
          supplierTeacherKiosk: f.supplierTeacherKiosk,
          useAliiInvoicePostingDate: f.useAliiInvoicePostingDate,
          attachAliiInvoiceToTass: f.attachAliiInvoiceToTass,
          ignoreDueDate: f.ignoreDueDate,
        }),
      });
      if (res.ok) {
        showToast({ title: 'Success', message: 'Config saved', type: 'success' });
        await load(true, false);
      } else {
        showToast({ title: 'Error', message: 'Failed to save config', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to save config', type: 'error' });
    } finally {
      isSavingConfig = { ...isSavingConfig, [entity.id]: false };
    }
  }

  async function forceSync() {
    isForcingSync = true;
    try {
      const response = await fetchWithAuth(`/api/tass-v2/clients/${clientId}/force-sync`, { method: 'POST' });
      showToast(
        response.ok ? { title: 'Success', message: 'Tass sync has been queued', type: 'success' } : { title: 'Error', message: 'Failed to queue Tass sync', type: 'error' }
      );
    } catch {
      showToast({ title: 'Error', message: 'Failed to queue Tass sync', type: 'error' });
    } finally {
      isForcingSync = false;
    }
  }

  async function disconnect(entity: EntityRow) {
    if (!confirm(`Disconnect Tass integration for "${entity.name}"?`)) return;
    try {
      const res = await fetchWithAuth(`/api/tass-v2/${clientId}/${entity.id}`, { method: 'DELETE' });
      if (res.ok) {
        showToast({ title: 'Success', message: `Disconnected ${entity.name}`, type: 'success' });
        expandedId = null;
        await load();
      } else {
        showToast({ title: 'Error', message: 'Failed to disconnect', type: 'error' });
      }
    } catch {
      showToast({ title: 'Error', message: 'Failed to disconnect', type: 'error' });
    }
  }
</script>

<div class="p-5">
  <h3 class="mb-1 text-xl font-semibold">Tass V2 Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure Tass V2 credentials for each Alii Entity.</p>

  <ClientIntegrationSettings basePath="/api/tass-v2" {clientId} />
  <div class="mb-4 flex justify-end gap-2">
    <Button label={isForcingSync ? 'Queueing...' : 'Force sync'} onClick={forceSync} disabled={isLoading || isForcingSync || !hasConfiguredEntities} size="small" />
    <Button label={isLoading ? 'Loading...' : 'Refresh'} onClick={load} disabled={isLoading || isForcingSync} colour="secondary" size="small" />
  </div>

  <h4 class="mb-3 text-lg font-semibold text-gray-800">Entities</h4>
  {#if isLoading}
    <p class="text-sm text-gray-500">Loading entities...</p>
  {:else if entities.length === 0}
    <p class="text-sm text-gray-500">No entities found for this client.</p>
  {:else}
    <div class="space-y-2">
      {#each entities as entity (entity.id)}
        <div class="overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
          <!-- Row header -->
          <button type="button" class="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-100" on:click={() => openRow(entity)}>
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium">{entity.name}</span>
              {#if entity.integration && entity.integration.hasEntityConfig}
                <span class="text-xs text-gray-500">{entity.integration.tassCompanyCode}</span>
                <span class="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">Configured</span>
              {:else if entity.integration && !entity.integration.hasEntityConfig}
                <span class="text-xs text-gray-500">{entity.integration.tassCompanyCode}</span>
                <span class="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">Missing entity config</span>
              {:else}
                <span class="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500">Not configured</span>
              {/if}
              {#if entity.integration?.lastSyncRunUtc}
                <span class="text-xs text-gray-400">Last sync: {entity.integration.lastSyncRunUtc}</span>
              {/if}
            </div>
            <span class="ml-2 text-sm text-gray-400">{expandedId === entity.id ? '▲' : '▼'}</span>
          </button>

          {#if expandedId === entity.id && credForms[entity.id] && configForms[entity.id]}
            <div class="border-t border-gray-200 px-4 pb-4 pt-4">
              {#if entity.integration && !entity.integration.hasEntityConfig}
                <div class="mb-5 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                  <p class="mb-1 font-semibold">Entity config missing.</p>
                  <p>
                    Credentials are already configured. Complete the <strong>Entity Config</strong> section below and click <strong>Create</strong> before this entity can sync.
                  </p>
                </div>
              {/if}

              <div class="space-y-6">
                {#if entity.integration}
                  <SyncSettings
                    settings={credForms[entity.id].syncSettings}
                    customSettings={customSyncSettings}
                    disabled={isSavingCred[entity.id]}
                    isSaving={isSavingCred[entity.id]}
                    onChange={() => saveCred(entity, true)}
                  />
                {/if}

                <!-- Form 1: Credentials -->
                <div class="space-y-4">
                  <h5 class="border-b pb-2 text-sm font-semibold text-gray-700">Connection &amp; Credentials</h5>

                  <div>
                    <label for="url-{entity.id}" class="mb-1 flex items-center gap-2 text-sm font-medium">
                      Tass URL <RequiredBadge />
                    </label>
                    <FieldHint text="The base URL for this school's TASS API." />
                    <input
                      id="url-{entity.id}"
                      type="url"
                      bind:value={credForms[entity.id].tassUrl}
                      placeholder="https://example.tass.net.au/tassweb/api/"
                      class="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      class:border-gray-300={credForms[entity.id].tassUrl}
                      class:border-primary-200={!credForms[entity.id].tassUrl}
                      disabled={isSavingCred[entity.id]}
                    />
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label for="code-{entity.id}" class="mb-1 flex items-center gap-2 text-sm font-medium">
                        Company Number <RequiredBadge />
                      </label>
                      <FieldHint text="The TASS company number for this entity." />
                      <input
                        id="code-{entity.id}"
                        type="text"
                        bind:value={credForms[entity.id].tassCompanyCode}
                        class="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        class:border-gray-300={credForms[entity.id].tassCompanyCode}
                        class:border-primary-200={!credForms[entity.id].tassCompanyCode}
                        disabled={isSavingCred[entity.id]}
                      />
                    </div>
                    <div>
                      <label for="school-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">School Name</label>
                      <FieldHint text="A friendly name used to identify this TASS connection." />
                      <input
                        id="school-{entity.id}"
                        type="text"
                        bind:value={credForms[entity.id].tassSchoolName}
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        disabled={isSavingCred[entity.id]}
                      />
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <!-- App Code V2 -->
                    <div>
                      <label for="appv2-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">API06 Key</label>
                      <FieldHint text="The TASS API06 application key." />
                      <div class="relative">
                        {#if credForms[entity.id].showAppCodeV2}
                          <input
                            id="appv2-{entity.id}"
                            type="text"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassApplicationCodeVersion2}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {:else}
                          <input
                            id="appv2-{entity.id}"
                            type="password"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassApplicationCodeVersion2}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {/if}
                        <button
                          type="button"
                          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          on:click={() => (credForms[entity.id].showAppCodeV2 = !credForms[entity.id].showAppCodeV2)}
                        >
                          {#if credForms[entity.id].showAppCodeV2}<EyeSlash size={16} />{:else}<Eye size={16} />{/if}
                        </button>
                      </div>
                    </div>
                    <!-- Token V2 -->
                    <div>
                      <label for="tokv2-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">API06 Token</label>
                      <FieldHint text="The TASS API06 token paired with the application key." />
                      <div class="relative">
                        {#if credForms[entity.id].showTokenKeyV2}
                          <input
                            id="tokv2-{entity.id}"
                            type="text"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassTokenKeyVersion2}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {:else}
                          <input
                            id="tokv2-{entity.id}"
                            type="password"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassTokenKeyVersion2}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {/if}
                        <button
                          type="button"
                          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          on:click={() => (credForms[entity.id].showTokenKeyV2 = !credForms[entity.id].showTokenKeyV2)}
                        >
                          {#if credForms[entity.id].showTokenKeyV2}<EyeSlash size={16} />{:else}<Eye size={16} />{/if}
                        </button>
                      </div>
                    </div>
                    <!-- App Code V3 -->
                    <div>
                      <label for="appv3-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">API22 Key</label>
                      <FieldHint text="The TASS API22 application key." />
                      <div class="relative">
                        {#if credForms[entity.id].showAppCodeV3}
                          <input
                            id="appv3-{entity.id}"
                            type="text"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassApplicationCodeVersion3}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {:else}
                          <input
                            id="appv3-{entity.id}"
                            type="password"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassApplicationCodeVersion3}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {/if}
                        <button
                          type="button"
                          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          on:click={() => (credForms[entity.id].showAppCodeV3 = !credForms[entity.id].showAppCodeV3)}
                        >
                          {#if credForms[entity.id].showAppCodeV3}<EyeSlash size={16} />{:else}<Eye size={16} />{/if}
                        </button>
                      </div>
                    </div>
                    <!-- Token V3 -->
                    <div>
                      <label for="tokv3-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">API22 Token</label>
                      <FieldHint text="The TASS API22 token paired with the application key." />
                      <div class="relative">
                        {#if credForms[entity.id].showTokenKeyV3}
                          <input
                            id="tokv3-{entity.id}"
                            type="text"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassTokenKeyVersion3}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {:else}
                          <input
                            id="tokv3-{entity.id}"
                            type="password"
                            autocomplete="off"
                            bind:value={credForms[entity.id].tassTokenKeyVersion3}
                            class="w-full rounded-md border border-gray-300 px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            disabled={isSavingCred[entity.id]}
                          />
                        {/if}
                        <button
                          type="button"
                          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          on:click={() => (credForms[entity.id].showTokenKeyV3 = !credForms[entity.id].showTokenKeyV3)}
                        >
                          {#if credForms[entity.id].showTokenKeyV3}<EyeSlash size={16} />{:else}<Eye size={16} />{/if}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="flex justify-end gap-2 pt-1">
                    <Button
                      label={isSavingCred[entity.id] ? 'Saving...' : entity.integration ? 'Update' : 'Create'}
                      onClick={() => saveCred(entity)}
                      disabled={!credForms[entity.id].tassUrl || !credForms[entity.id].tassCompanyCode || !!isSavingCred[entity.id]}
                      colour="primary"
                      size="small"
                    />
                  </div>
                </div>

                {#if entity.integration}
                  <!-- Form 2: Entity Config (TassEntityOptions) -->
                  <div class="space-y-4">
                    <h5 class="border-b pb-2 text-sm font-semibold text-gray-700">Entity Config</h5>

                    <div class="grid grid-cols-3 gap-3">
                      <div>
                        <label for="budget-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Budget Number</label>
                        <FieldHint text="The TASS budget number used for this entity." />
                        <input
                          id="budget-{entity.id}"
                          type="number"
                          min="1"
                          bind:value={configForms[entity.id].budgetNumber}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          disabled={isSavingConfig[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="gltype-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">GL Code Type</label>
                        <FieldHint text="Optional TASS general ledger code type to synchronise." />
                        <input
                          id="gltype-{entity.id}"
                          type="text"
                          bind:value={configForms[entity.id].generalLedgerCodeType}
                          placeholder="e.g. GLCode"
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          disabled={isSavingConfig[entity.id]}
                        />
                      </div>
                      <div>
                        <label for="merge-{entity.id}" class="mb-1 block text-sm font-medium text-gray-700">Merge Entity Department</label>
                        <FieldHint text="Optional department used when merging entity data." />
                        <input
                          id="merge-{entity.id}"
                          type="text"
                          bind:value={configForms[entity.id].mergeEntityDepartment}
                          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                          disabled={isSavingConfig[entity.id]}
                        />
                      </div>
                    </div>

                    <FieldHint text="Use these options to tailor how invoices and suppliers are handled for this entity." />
                    <div class="grid gap-x-6 gap-y-2 pt-1 md:grid-cols-2">
                      <div>
                        <label class="flex cursor-pointer items-center gap-2 text-sm">
                          <input type="checkbox" bind:checked={configForms[entity.id].supplierTeacherKiosk} class="rounded" disabled={isSavingConfig[entity.id]} />
                          Staff Kiosk Suppliers Only
                        </label>
                        <FieldHint text="Sync only suppliers available in the TASS Staff Kiosk." />
                      </div>
                      <div>
                        <label class="flex cursor-pointer items-center gap-2 text-sm">
                          <input type="checkbox" bind:checked={configForms[entity.id].useAliiInvoicePostingDate} class="rounded" disabled={isSavingConfig[entity.id]} />
                          Use Alii Invoice Posting Date
                        </label>
                        <FieldHint text="Use the Alii invoice posting date for TASS transactions." />
                      </div>
                      <div>
                        <label class="flex cursor-pointer items-center gap-2 text-sm">
                          <input type="checkbox" bind:checked={configForms[entity.id].attachAliiInvoiceToTass} class="rounded" disabled={isSavingConfig[entity.id]} />
                          Attach Alii Invoice to Tass
                        </label>
                        <FieldHint text="Attach the Alii invoice or expense claim file to its TASS transaction." />
                      </div>
                      <div>
                        <label class="flex cursor-pointer items-center gap-2 text-sm">
                          <input type="checkbox" bind:checked={configForms[entity.id].ignoreDueDate} class="rounded" disabled={isSavingConfig[entity.id]} />
                          Ignore Due Date
                        </label>
                        <FieldHint text="Do not send a due date when posting invoices or expense claims to TASS." />
                      </div>
                    </div>

                    <div class="flex justify-end pt-1">
                      <Button
                        label={isSavingConfig[entity.id] ? 'Saving...' : entity.integration.hasEntityConfig ? 'Update' : 'Create'}
                        onClick={() => saveConfig(entity)}
                        disabled={!!isSavingConfig[entity.id]}
                        colour="primary"
                        size="small"
                      />
                    </div>
                  </div>

                  <div class="rounded-lg border border-red-200 bg-red-50 p-4">
                    <h5 class="mb-1 text-sm font-semibold text-red-700">Danger Zone</h5>
                    <p class="mb-3 text-xs text-red-600">
                      Deleting this integration removes all credentials, config and sync state for <strong>{entity.name}</strong>. This cannot be undone.
                    </p>
                    <Button label="Delete" onClick={() => disconnect(entity)} colour="error" size="small" />
                  </div>
                {/if}
              </div>
              <!-- /space-y-6 -->
            </div>
            <!-- /border-t panel -->
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>
